import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../api/client';
import Composer from '../components/chat/Composer';

const newClientMsgId = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

/**
 * Standalone answer page — the target of the Telegram notification's web_url,
 * so this route is live in the wild and cannot simply be folded into the
 * session page. It shares the session page's Composer so the two surfaces
 * cannot drift apart.
 */
export default function HumanRequestPage() {
  const { sessionId } = useParams();
  const [requests, setRequests] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const genRef = useRef(0);

  const load = useCallback(async () => {
    const gen = ++genRef.current;
    try {
      const list = await api.listHumanRequests(sessionId);
      if (gen !== genRef.current) return;
      setRequests(list);
      setLoadError(null);
    } catch (e) {
      if (gen !== genRef.current) return;
      setLoadError(e);
    } finally {
      if (gen === genRef.current) setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => { load(); }, [load]);

  // Every pending request, oldest first — the old code took only the first
  // match, which made a second pending question invisible.
  const pendingAll = requests
    .filter(r => r.status === 'pending')
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  const pending = pendingAll[0];

  const submit = async (text) => {
    if (!pending) return;
    setSending(true);
    setSendError(null);
    ++genRef.current;                      // any in-flight fetch is now stale
    try {
      await api.answerHumanRequest(pending.id, text, newClientMsgId());
      await load();
    } catch (e) {
      setSendError(e);
      throw e;
    } finally {
      setSending(false);
    }
  };

  if (loading) return <div className="loading">Загрузка...</div>;
  if (loadError && requests.length === 0) {
    return <div className="alert alert-error">Не удалось загрузить запросы: {loadError.message}</div>;
  }
  if (!pending) return <div className="empty">Нет активных запросов</div>;

  return (
    <div style={{ maxWidth: 600, margin: '0 auto' }}>
      <div className="card">
        <h2 className="card-title" style={{ marginBottom: 16 }}>Требуется ваше решение</h2>
        <Composer
          mode="answer"
          question={{
            text: pending.question,
            context_summary: pending.context_summary,
            options: pending.options,
          }}
          extraPendingCount={pendingAll.length - 1}
          onSubmit={submit}
          sending={sending}
          error={sendError}
        />
      </div>
    </div>
  );
}
